/**
 * Full-duplex PCM audio for both training modes (Playbook Live and
 * knowledge-base training).
 *
 * The microphone stays OPEN while the customer is speaking. There is no
 * silence gate and no turn-taking here — the voice model does its own,
 * natively, and any gate we added would put the half-duplex feel back.
 *
 * THE TRAP THIS FILE EXISTS TO AVOID: with the mic open during playback, a
 * laptop speaker feeds the customer's own voice back into the session, which
 * transcribes it as the salesperson and degrades into the model arguing with
 * its own echo. Browser AEC handles most of it; `probeEcho` catches the rest
 * before a session starts.
 *
 * Worklets are compiled from inline source via a Blob URL so this needs no
 * build-config or public/ asset changes.
 *
 * ⚠️ MIC FRAMES MUST FLOW CONTINUOUSLY, INCLUDING SILENCE, FROM THE MOMENT THE
 * SESSION OPENS. Do not "optimise" this by sending only when the rep is
 * speaking. The AI customer greets first, and it will not produce that greeting
 * unless an input stream is live — a session with no inbound audio opens in
 * silence and waits forever. Verified against the live API: no stream, no
 * greeting; streaming silence, greeting at 0.8s.
 */

export const SAMPLE_RATE = 24000;

/** Capture worklet: forwards raw Float32 frames to the main thread. */
const CAPTURE_WORKLET = `
class CaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel && channel.length) {
      this.port.postMessage(new Float32Array(channel));
    }
    return true;
  }
}
registerProcessor('pcm-capture', CaptureProcessor);
`;

/**
 * Playback worklet: a ring buffer the socket fills and the audio clock drains.
 * Necessary because WebSocket frames arrive in bursts while the output device
 * pulls at a fixed rate; without the buffer every jitter spike is an audible gap.
 */
const PLAYBACK_WORKLET = `
class PlaybackProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.queue = [];
    this.offset = 0;
    this.port.onmessage = (event) => {
      if (event.data === 'flush') {
        this.queue = [];
        this.offset = 0;
        return;
      }
      this.queue.push(event.data);
    };
  }
  process(_inputs, outputs) {
    const output = outputs[0][0];
    let written = 0;
    while (written < output.length && this.queue.length) {
      const chunk = this.queue[0];
      const take = Math.min(chunk.length - this.offset, output.length - written);
      output.set(chunk.subarray(this.offset, this.offset + take), written);
      written += take;
      this.offset += take;
      if (this.offset >= chunk.length) {
        this.queue.shift();
        this.offset = 0;
      }
    }
    // Silence rather than stale audio when the buffer runs dry.
    if (written < output.length) output.fill(0, written);
    return true;
  }
}
registerProcessor('pcm-playback', PlaybackProcessor);
`;

const workletUrl = (source: string): string =>
  URL.createObjectURL(new Blob([source], { type: 'application/javascript' }));

const floatToPcm16 = (input: Float32Array): ArrayBuffer => {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, input[i]));
    output[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return output.buffer;
};

const pcm16ToFloat = (buffer: ArrayBuffer): Float32Array => {
  const input = new Int16Array(buffer);
  const output = new Float32Array(input.length);
  for (let i = 0; i < input.length; i += 1) output[i] = input[i] / 0x8000;
  return output;
};

const rms = (samples: Float32Array): number => {
  let total = 0;
  for (let i = 0; i < samples.length; i += 1) total += samples[i] * samples[i];
  return Math.sqrt(total / Math.max(1, samples.length));
};

export interface EchoProbeResult {
  /** Ratio of mic energy during playback to ambient. Above ~3 means the mic is
   *  hearing the speakers and the session needs a headset. */
  ratio: number;
  isEchoLikely: boolean;
  ambient: number;
  duringTone: number;
}

export interface PlaybookLiveAudioHandlers {
  onMicChunk: (pcm: ArrayBuffer) => void;
  onLevel?: (level: number) => void;
  onError?: (message: string) => void;
}

export class PlaybookLiveAudio {
  private context: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private captureNode: AudioWorkletNode | null = null;
  private playbackNode: AudioWorkletNode | null = null;
  private handlers: PlaybookLiveAudioHandlers | null = null;
  private micMuted = false;

  get isRunning(): boolean {
    return this.context !== null;
  }

  async start(handlers: PlaybookLiveAudioHandlers): Promise<void> {
    this.handlers = handlers;

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // All three matter here. Without echoCancellation the customer hears
        // itself through the laptop speakers.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: SAMPLE_RATE,
      },
    });

    this.context = new AudioContext({ sampleRate: SAMPLE_RATE });
    if (this.context.state === 'suspended') await this.context.resume();

    const captureUrl = workletUrl(CAPTURE_WORKLET);
    const playbackUrl = workletUrl(PLAYBACK_WORKLET);
    try {
      await this.context.audioWorklet.addModule(captureUrl);
      await this.context.audioWorklet.addModule(playbackUrl);
    } finally {
      URL.revokeObjectURL(captureUrl);
      URL.revokeObjectURL(playbackUrl);
    }

    const source = this.context.createMediaStreamSource(this.stream);
    this.captureNode = new AudioWorkletNode(this.context, 'pcm-capture');
    this.captureNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
      const samples = event.data;
      this.handlers?.onLevel?.(rms(samples));
      if (!this.micMuted) this.handlers?.onMicChunk(floatToPcm16(samples));
    };
    source.connect(this.captureNode);
    // Capture must be pulled by the graph to run, but must not be audible —
    // routing it to a silent gain keeps it alive without creating a loop.
    const silentSink = this.context.createGain();
    silentSink.gain.value = 0;
    this.captureNode.connect(silentSink).connect(this.context.destination);

    this.playbackNode = new AudioWorkletNode(this.context, 'pcm-playback', {
      outputChannelCount: [1],
    });
    this.playbackNode.connect(this.context.destination);
  }

  /** Queue a customer audio frame for playback. */
  playChunk(pcm: ArrayBuffer): void {
    if (!this.playbackNode) return;
    const samples = pcm16ToFloat(pcm);
    this.playbackNode.port.postMessage(samples, [samples.buffer]);
  }

  /** Drop everything queued — used when the rep interrupts. */
  flushPlayback(): void {
    this.playbackNode?.port.postMessage('flush');
  }

  setMicMuted(muted: boolean): void {
    this.micMuted = muted;
  }

  /**
   * Play a short tone and measure how much of it comes back through the mic.
   * Run before a session: a laptop-speaker setup that fails this will produce
   * a session where the customer talks to itself.
   */
  async probeEcho(): Promise<EchoProbeResult> {
    const context = new AudioContext({ sampleRate: SAMPLE_RATE });
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    try {
      const analyser = context.createAnalyser();
      analyser.fftSize = 2048;
      context.createMediaStreamSource(stream).connect(analyser);
      const buffer = new Float32Array(analyser.fftSize);

      const measure = (): number => {
        analyser.getFloatTimeDomainData(buffer);
        return rms(buffer);
      };
      const sample = async (durationMs: number): Promise<number> => {
        const readings: number[] = [];
        const until = performance.now() + durationMs;
        while (performance.now() < until) {
          readings.push(measure());
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        return readings.reduce((a, b) => a + b, 0) / Math.max(1, readings.length);
      };

      const ambient = await sample(600);

      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 440;
      gain.gain.value = 0.25;
      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      const duringTone = await sample(900);
      oscillator.stop();

      const floor = Math.max(ambient, 1e-5);
      const ratio = duringTone / floor;
      return { ratio, isEchoLikely: ratio > 3, ambient, duringTone };
    } finally {
      stream.getTracks().forEach((track) => track.stop());
      await context.close();
    }
  }

  async stop(): Promise<void> {
    this.captureNode?.port.close();
    this.captureNode?.disconnect();
    this.playbackNode?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    if (this.context && this.context.state !== 'closed') await this.context.close();
    this.context = null;
    this.stream = null;
    this.captureNode = null;
    this.playbackNode = null;
    this.handlers = null;
  }
}

export const playbookLiveAudio = new PlaybookLiveAudio();
